import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaSearch, FaTimes, FaDownload, FaPlus, FaEdit, FaTrashAlt, FaSitemap } from 'react-icons/fa';
import {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  exportApprovalFlows,
  deleteApprovalFlow,
  getAllApprovalFlows,
} from 'redux/booking/travel/setting/approvalFlowSlice';
import { debounce } from 'helpers/debounce';
import { Card, AgGridTable, Pagination, Button, Confirmation } from 'components/common';
import ApprovalFlowForm from 'components/app/bookings/travel/setting/approval-flow/ApprovalFlowForm';

const SEARCH_KEY = 'site_name_cont';

const NamesCell = ({ value }) => (
  <span className="tw-flex tw-flex-wrap tw-gap-1">
    {(Array.isArray(value) ? value : [value]).filter(Boolean).map((n, i) => (
      <span key={i} className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-bg-slate-100 tw-text-slate-700">{n}</span>
    ))}
  </span>
);
NamesCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string])
};

const ApprovalFlowPage = () => {
  const dispatch = useDispatch();
  const entry = useSelector((s) => s.booking_setting_approval_flow.allData) || {};
  const loading = useSelector((s) => s.booking_setting_approval_flow.loading);
  const exporting = useSelector((s) => s.booking_setting_approval_flow.exporting);

  const { data = [], totalRows = 0, totalPagesCount = 1, page = 1, pageSize = 50, currentParams = {} } = entry;

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('New Approval Flow');
  const [showDelete, setShowDelete] = useState(false);

  const searchRef = useRef('');
  const perPageRef = useRef(pageSize);
  useEffect(() => { perPageRef.current = pageSize; }, [pageSize]);

  const record = selected.at(-1) || {};

  const columnDefs = useMemo(() => [
    { headerName: 'ID#', field: 'id', minWidth: 90 },
    { headerName: 'Site', field: 'site_name', minWidth: 180 },
    { headerName: 'HR admin site office', field: 'hr_admins_site_office_confirm', minWidth: 200, cellRenderer: NamesCell },
    { headerName: 'Chief Rep/GM approval', field: 'chief_rep_gm_approval', minWidth: 180 },
    { headerName: 'FNC check', field: 'accountants_confirm', minWidth: 180, cellRenderer: NamesCell },
    { headerName: 'C&B check', field: 'c_b_verify', minWidth: 180, cellRenderer: NamesCell },
    { headerName: 'HR admin head office', field: 'hr_admins_head_office_confirm', minWidth: 200, cellRenderer: NamesCell },
    { headerName: 'HR manager approval', field: 'hr_manager_approval', minWidth: 180 },
    { headerName: 'GD Approval', field: 'gd_approval', minWidth: 160 },
  ], []);

  const runFetch = useCallback((pageNum) => {
    const params = { page: pageNum, per_page: perPageRef.current, ...(searchRef.current ? { [SEARCH_KEY]: searchRef.current } : {}) };
    dispatch(setNestedPage({ key: 'allData', page: pageNum }));
    dispatch(setNestedParams({ key: 'allData', params }));
    dispatch(getAllApprovalFlows(params));
  }, [dispatch]);

  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current) { didInit.current = true; dispatch(getAllApprovalFlows(currentParams)); }
  }, [dispatch, currentParams]);

  const debouncedSearch = useRef(debounce(() => runFetch(1), 500)).current;
  const handleSearch = (e) => { const v = e.target.value; setSearch(v); searchRef.current = v; debouncedSearch(); };
  const handleClearSearch = () => { setSearch(''); searchRef.current = ''; runFetch(1); };
  const handlePageChange = (p) => runFetch(p);
  const handlePageSizeChange = (sz) => { perPageRef.current = sz; dispatch(setNestedPageSize({ key: 'allData', pageSize: sz })); runFetch(1); };
  const handleExport = () => dispatch(exportApprovalFlows({ ...currentParams }));

  const requireOne = () => {
    if (selected.length === 0) { toast.error('Record has not selected yet.'); return false; }
    if (selected.length > 1) { toast.error('You are selecting multiple records. Please select just one record.'); return false; }
    return true;
  };

  const handleNew = () => { setFormTitle('New Approval Flow'); setShowForm(true); };
  const handleEdit = () => { if (!requireOne()) return; setFormTitle('Edit Approval Flow'); setShowForm(true); };
  const handleDelete = () => { if (selected.length === 0) { toast.error('Record has not selected yet.'); return; } setShowDelete(true); };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
        <div className="tw-flex tw-items-center tw-gap-2">
          <FaSitemap className="tw-text-blue-600" size={20} />
          <div>
            <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Travel Approval Flows</h1>
            <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Configure the multi-level approval chain per site office.</p>
          </div>
        </div>
      </div>

      <Card padding="none">
        <div className="tw-p-4">
          <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-mb-3">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
              <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-64 sm:tw-flex-shrink-0">
                <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
                <input type="text" value={search} onChange={handleSearch} placeholder="Site..."
                  className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-solid tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700" />
                {search && (
                  <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0">
                    <FaTimes size={8} />
                  </button>
                )}
              </div>
              <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
              <Button variant="primary" size="sm" leftIcon={FaPlus} onClick={handleNew}>New</Button>
              <Button variant="secondary" size="sm" leftIcon={FaEdit} onClick={handleEdit}>Edit</Button>
              <Button variant="danger" size="sm" leftIcon={FaTrashAlt} onClick={handleDelete}>Delete</Button>
              <Button variant="secondary" size="sm" leftIcon={FaDownload} loading={exporting} onClick={handleExport}>Export</Button>
              <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
                {(totalRows || 0).toLocaleString()} records{selected.length ? ` • ${selected.length} selected` : ''}
              </span>
            </div>
          </div>

          <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
            <AgGridTable rowData={data} columnDefs={columnDefs} pagination={false} filter={false} multiSelect height="60vh" loading={loading} onSelectionChanged={setSelected} />
            <Pagination currentPage={page} totalPages={totalPagesCount} totalResults={totalRows} perPage={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} pageSizeOptions={[50, 100, 200]} />
          </div>
        </div>
      </Card>

      {showForm && <ApprovalFlowForm showModal={showForm} setshowModal={setShowForm} title={formTitle} record={formTitle === 'New Approval Flow' ? {} : record} />}

      <Confirmation
        show={showDelete}
        type="danger"
        title="Delete approval flow"
        message="Are you sure to delete?"
        confirmText="Delete"
        onConfirm={() => { dispatch(deleteApprovalFlow(record.id)); setShowDelete(false); }}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
};

export default ApprovalFlowPage;
